import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../accordion';

interface AdvancedSectionProps {
  title: string;
  titleIcon?: React.ReactNode;
  children: React.ReactNode;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
}

/**
 * AdvancedSection component that wraps content in a collapsible accordion
 * https://www.figma.com/design/8MHmVdfscl5k2kpTqf4UXX/%F0%9F%8F%B4%E2%80%8D%E2%98%A0%EF%B8%8F--M-shadcn-ui?node-id=6962-15439&t=ukWulEfWACSSr4sP-0
 * @param title - The title text to display in the accordion trigger
 * @param titleIcon - Optional icon to display alongside the title
 * @param children - The content to display inside the accordion
 * @param defaultValue - The default value for uncontrolled accordion (use "item-1" to start open)
 * @param value - The controlled value for the accordion
 * @param onValueChange - Callback fired when the accordion value changes
 * @param disabled - Whether the accordion trigger is disabled
 */
export const AdvancedSection = ({
  title,
  titleIcon,
  children,
  defaultValue,
  value,
  onValueChange,
  disabled,
}: AdvancedSectionProps) => {
  return (
    <Accordion
      type="single"
      collapsible
      className="w-full overflow-hidden"
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
    >
      <AccordionItem
        value="item-1"
        className="rounded-md border-bg-muted-light bg-bg-muted-light px-1 py-1"
      >
        <AccordionTrigger
          className="flex items-center rounded-sm px-3 hover:border-bg-muted-light hover:bg-highlight-background hover:no-underline [data-state=closed]:rounded-b-sm [&>svg]:ml-auto"
          disabled={disabled}
        >
          {titleIcon && <span className="text-base text-foreground">{titleIcon}</span>}
          <span className="text-base font-medium text-foreground">{title}</span>
        </AccordionTrigger>
        <AccordionContent className="overflow-x-auto bg-bg-muted-light px-6 pb-6">
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
